/**
 * Hooks to make the browser/mobile back button behave nicely.
 *
 *  - useBackToHome(): on a deep-link page, injects a "/" entry behind the
 *    current page so the system back button always returns to the index.
 *    Detects a deep link via `document.referrer` not being the same origin.
 *
 *  - useDialogBack(open, onClose): pushes a sentinel history entry when a
 *    dialog opens and dismisses the dialog when the user presses the
 *    system back gesture / browser back button.
 */
import { useEffect } from "react";

function isDeepLink() {
  // No referrer at all → typed URL, new tab, or refresh.
  // Referrer from another origin → came from outside the app.
  // Same-origin referrer → in-app navigation, leave history alone.
  if (typeof document === "undefined" || typeof window === "undefined") {
    return false;
  }
  const ref = document.referrer || "";
  if (!ref) return true;
  try {
    const refUrl = new URL(ref);
    return refUrl.origin !== window.location.origin;
  } catch (_e) {
    return true;
  }
}

export function useBackToHome() {
  useEffect(() => {
    if (!isDeepLink()) return;
    if (window.location.pathname === "/") return;
    const here = window.location.pathname + window.location.search;
    try {
      // Replace the current entry with "/", then push the original URL.
      // After this, the browser back stack is ['/', here].
      window.history.replaceState(window.history.state, "", "/");
      window.history.pushState(window.history.state, "", here);
    } catch {
      // History API not available in this context (e.g. sandboxed iframe).
    }
  }, []);
}

export function useDialogBack(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const token = "dlg-" + Date.now();
    try {
      window.history.pushState({ __dialog: token }, "");
    } catch (_e) {
      return undefined;
    }
    const onPop = () => onClose();
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // If our sentinel is still on top (dialog closed via the UI, not via back),
      // pop it so the history stack stays clean.
      if (
        window.history.state &&
        typeof window.history.state === "object" &&
        window.history.state.__dialog === token
      ) {
        window.history.back();
      }
    };
  }, [open, onClose]);
}
